import type { Router, RouteRecordRaw } from 'vue-router'

/**
 * 商城独立窗隐藏页（详情/表单）挂载约定
 *
 * 列表在后台菜单树的 /mall（及 /ai-Application-center/mall）Layout 下，
 * 独立窗靠该 Layout → StandaloneAppShell 显示菜单。
 *
 * 若再挂 /mall/product、/mall/trade 等「Layout 岛」，一点详情就换父级 Layout，
 * 整页无菜单。隐藏子路由必须挂到与列表相同的 /mall 根上。
 *
 * DIY 全屏编辑器（/diy/template/editor）故意不套 Layout，不在此列。
 */

type MallHiddenChild = {
  /** 相对 /mall 的 path，如 product/spu/detail/:id(\\d+) */
  path: string
  /** 裸路由 name；前缀副本自动加 AiAppCenter */
  name: string
  component: RouteRecordRaw['component']
  meta: Record<string, unknown>
}

const MALL_HIDDEN_CHILDREN: MallHiddenChild[] = [
  {
    path: 'product/spu/add',
    name: 'ProductSpuAdd',
    component: () => import('@/views/mall/product/spu/form/index.vue'),
    meta: {
      noCache: false,
      hidden: true,
      canTo: true,
      icon: 'ep:edit',
      title: '商品添加',
      activeMenu: '/mall/product/spu'
    }
  },
  {
    path: 'product/spu/edit/:id(\\d+)',
    name: 'ProductSpuEdit',
    component: () => import('@/views/mall/product/spu/form/index.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      icon: 'ep:edit',
      title: '商品编辑',
      activeMenu: '/mall/product/spu'
    }
  },
  {
    path: 'product/spu/detail/:id(\\d+)',
    name: 'ProductSpuDetail',
    component: () => import('@/views/mall/product/spu/form/index.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      icon: 'ep:view',
      title: '商品详情',
      activeMenu: '/mall/product/spu'
    }
  },
  {
    path: 'product/property/value/:propertyId(\\d+)',
    name: 'ProductPropertyValue',
    component: () => import('@/views/mall/product/property/value/index.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      icon: 'ep:view',
      title: '商品属性值',
      activeMenu: '/mall/product/property'
    }
  },
  // M9 游戏抽奖：编辑 / 详情 / 预览（挂商城营销中心，侧栏不丢）
  {
    path: 'promotion/game-lottery/activity/edit/:id?',
    name: 'MallGameLotteryActivityEdit',
    component: () => import('@/views/promotion/activity/PortalEditorPage.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '活动编辑',
      activeMenu: '/mall/promotion/game-lottery/activity-manage'
    }
  },
  {
    path: 'promotion/game-lottery/activity/detail/:id(\\d+)',
    name: 'MallGameLotteryActivityDetail',
    component: () => import('@/views/promotion/activity/Detail.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '活动详情',
      activeMenu: '/mall/promotion/game-lottery/activity-manage'
    }
  },
  {
    path: 'promotion/game-lottery/activity/preview/:id(\\d+)',
    name: 'MallGameLotteryActivityPreview',
    component: () => import('@/views/promotion/activity/Preview.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '活动预览',
      activeMenu: '/mall/promotion/game-lottery/activity-manage'
    }
  },
  {
    path: 'trade/order/detail/:id(\\d+)',
    name: 'TradeOrderDetail',
    component: () => import('@/views/mall/trade/order/detail/index.vue'),
    meta: {
      title: '订单详情',
      icon: 'ep:view',
      hidden: true,
      canTo: true,
      activeMenu: '/mall/trade/order'
    }
  },
  {
    path: 'trade/after-sale/detail/:id(\\d+)',
    name: 'TradeAfterSaleDetail',
    component: () => import('@/views/mall/trade/afterSale/detail/index.vue'),
    meta: {
      title: '退款详情',
      icon: 'ep:view',
      hidden: true,
      canTo: true,
      activeMenu: '/mall/trade/after-sale'
    }
  },
  {
    // 菜单重组后：店铺管理挂在 /mall/merchant 下
    path: 'merchant/shop-manager/merchant-apply/detail/:id(\\d+)',
    name: 'MerchantApplyDetail',
    component: () => import('@/views/mall/apply/detail.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '入驻审核详情',
      activeMenu: '/mall/merchant/shop-manager/merchant-apply'
    }
  },
  {
    // 旧书签兼容：/mall/shop-manager/merchant-apply/detail/:id
    path: 'shop-manager/merchant-apply/detail/:id(\\d+)',
    name: 'MerchantApplyDetailLegacy',
    component: () => import('@/views/mall/apply/detail.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '入驻审核详情',
      activeMenu: '/mall/merchant/shop-manager/merchant-apply'
    }
  },
  // 店铺形象：正式挂商家中心 → /mall/merchant/shop-profile；旧根路径保留兜底
  {
    path: 'merchant/shop-profile',
    name: 'MallShopProfile',
    component: () => import('@/views/mall/shop-profile/index.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '店铺形象',
      activeMenu: '/mall/merchant/shop-profile'
    }
  },
  {
    path: 'shop-profile',
    name: 'MallShopProfileLegacy',
    component: () => import('@/views/mall/shop-profile/index.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '店铺形象',
      activeMenu: '/mall/merchant/shop-profile'
    }
  },
  // 充值套餐：正式挂交易中心 → /mall/trade/wallet-recharge-package；旧路径保留兜底
  {
    path: 'trade/wallet-recharge-package',
    name: 'MallWalletRechargePackageFallback',
    component: () => import('@/views/pay/wallet/rechargePackage/index.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '充值套餐',
      activeMenu: '/mall/trade/wallet-recharge-package'
    }
  },
  {
    path: 'member/wallet-recharge-package',
    name: 'MallWalletRechargePackageLegacy',
    component: () => import('@/views/pay/wallet/rechargePackage/index.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '充值套餐',
      activeMenu: '/mall/trade/wallet-recharge-package'
    }
  },
  // 充值记录兜底：挂会员中心后正式 path=/mall/member/wallet-recharge
  {
    path: 'member/wallet-recharge',
    name: 'MallWalletRechargeFallback',
    component: () => import('@/views/pay/wallet/recharge/index.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '充值记录',
      activeMenu: '/mall/member/wallet-recharge'
    }
  },
  // 用户管理兜底：SQL 菜单未灌权时，软注入侧栏仍可打开列表（与 9002510 同页）
  {
    path: 'member/user',
    name: 'MallMemberUserFallback',
    component: () => import('@/views/member/user/index.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '用户列表',
      activeMenu: '/mall/member/user'
    }
  },
  {
    path: 'member/user/detail/:id',
    name: 'MallMemberUserDetailFallback',
    component: () => import('@/views/member/user/detail/index.vue'),
    meta: {
      noCache: true,
      hidden: true,
      canTo: true,
      title: '用户详情',
      activeMenu: '/mall/member/user'
    }
  }
]

const ALL_HIDDEN_NAMES = MALL_HIDDEN_CHILDREN.flatMap((c) => [
  c.name,
  `${c.name}AiAppCenter`
])

/** 同 path 可能有静态桩 + 动态菜单两套父级；隐藏页需都能挂上，否则 catch-all 父级会吞掉导航 */
function findMallLayoutParentNames(router: Router, rootPath: string): string[] {
  const roots = router.getRoutes().filter((r) => r.path === rootPath && r.name)
  if (!roots.length) return []

  const menuHint = (p: string) =>
    p === 'shop-manager' ||
    p.startsWith('shop-manager/') ||
    p === 'product' ||
    p.startsWith('product/') ||
    p === 'trade' ||
    p.startsWith('trade/')

  const ranked = [...roots].sort((a, b) => {
    const aMenu = (a.children || []).some((c) => menuHint(String(c.path || ''))) ? 1 : 0
    const bMenu = (b.children || []).some((c) => menuHint(String(c.path || ''))) ? 1 : 0
    if (bMenu !== aMenu) return bMenu - aMenu
    return (b.children?.length ?? 0) - (a.children?.length ?? 0)
  })
  return ranked.map((r) => String(r.name))
}

function toChildRecord(def: MallHiddenChild, name: string): RouteRecordRaw {
  return {
    path: def.path,
    name,
    component: def.component,
    meta: { ...def.meta }
  }
}

function parentHasChildPath(router: Router, parentName: string, childPath: string): boolean {
  const parent = router.getRoutes().find((r) => r.name === parentName)
  if (!parent?.children?.length) return false
  return parent.children.some((c) => String(c.path || '') === childPath)
}

/** 本轮 ensure 注册的隐藏路由 name，便于下次先卸再挂 */
const registeredMallHiddenNames = new Set<string>()

/**
 * 后台 /mall 菜单树就绪后调用：把商城隐藏页挂到与列表相同的 Layout 父级。
 * 对每个同 path 父级都尝试挂载（已有同 path 子路由则跳过），避免静态 catch-all 父级抢匹配后进不了动态父级上的页。
 */
export function ensureMallHiddenRoutes(router: Router) {
  for (const name of [...registeredMallHiddenNames, ...ALL_HIDDEN_NAMES]) {
    if (router.hasRoute(name)) {
      router.removeRoute(name)
    }
  }
  registeredMallHiddenNames.clear()

  const mountOnParents = (rootPath: string, nameBuilder: (def: MallHiddenChild, parentName: string, index: number) => string) => {
    const parents = findMallLayoutParentNames(router, rootPath)
    parents.forEach((parentName, index) => {
      for (const def of MALL_HIDDEN_CHILDREN) {
        if (parentHasChildPath(router, parentName, def.path)) {
          continue
        }
        const name = nameBuilder(def, parentName, index)
        router.addRoute(parentName, toChildRecord(def, name))
        registeredMallHiddenNames.add(name)
      }
    })
  }

  mountOnParents('/mall', (def, parentName, index) =>
    index === 0 ? def.name : `${def.name}__${parentName}`
  )
  mountOnParents('/ai-Application-center/mall', (def, parentName, index) =>
    index === 0 ? `${def.name}AiAppCenter` : `${def.name}AiAppCenter__${parentName}`
  )
}

/** @deprecated 使用 ensureMallHiddenRoutes */
export const ensureMallMerchantApplyDetailRoute = ensureMallHiddenRoutes
